import { useState } from 'react'
import clsx from 'clsx'
import { Eye, EyeOff } from 'lucide-react'
import { FormField } from '../base/FormField'
import type { TextInputProps } from './TextInput'

export const PasswordInput = ({
  name,
  label,
  placeholder = '••••••••',
  required,
  error,
  helperText,
  register,
  className,
  disabled = false
}: Omit<TextInputProps, 'type' | 'icon'>) => {
  const [showPassword, setShowPassword] = useState(false)
  const showError = !!error

  return (
    <FormField
      name={name}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      showError={showError}
    >
      <div className="relative">
        <input
          {...register}
          id={name}
          type={showPassword ? 'text' : 'password'}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="current-password"
          aria-invalid={showError || undefined}
          aria-describedby={showError && error ? `${name}-error` : undefined}
          className={clsx(
            'block w-full h-10 rounded-md px-3 pr-10 text-sm text-[var(--text-primary)] bg-[var(--surface-input)] transition-all duration-150 border focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)] hover:border-[var(--border-strong)] placeholder:text-[var(--text-muted)]',
            'border-[var(--border-input)]',
            showError && 'border-[var(--status-error-border)] focus-visible:ring-[var(--status-error-text)]',
            disabled && 'opacity-50 cursor-not-allowed bg-[var(--surface-input-disabled)]',
            className
          )}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-secondary)] focus:outline-none transition-colors"
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeOff className="w-4 h-4" />
          ) : (
            <Eye className="w-4 h-4" />
          )}
        </button>
      </div>
    </FormField>
  )
}

PasswordInput.displayName = 'PasswordInput'
